import {faqRefusalNotice, getFAQAnalyticsBootstrapUrl, loadFAQSectionData, sectionText} from './utils';
import React, {Component, useState, useRef, useEffect} from 'react';
import classNames from 'classnames/bind';
import '../../faq-sections.css';
import {map} from 'lodash';
import { trackWithBootstrap } from '../analytics/host-beacon';

let cx = classNames.bind({});

// Per mount: `aria-controls` must name exactly one answer on the page.
let answerSequence = 0;

// Expandable Article Component for the sequential layout with slide animation
const ExpandableArticleItem = (props) => {
    const [collapsed, setCollapsed] = useState(true);
    const [answerId] = useState(() => `hc-faq-full-answer-${++answerSequence}`);
    const [isAnimating, setIsAnimating] = useState(false);
    const contentRef = useRef(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (contentRef.current) {
            const scrollHeight = contentRef.current.scrollHeight;
            setHeight(collapsed ? 0 : scrollHeight);
        }
    }, [collapsed]);

    useEffect(() => {
        if (collapsed || !props.analyticsBootstrapUrl || !props.article.id) {
            return undefined;
        }

        trackWithBootstrap(props.analyticsBootstrapUrl, 'trackArticleView', {
            faqSectionId: props.sectionId,
            articleId: props.article.id,
            articleViewType: 'faq_expand',
        }).catch(() => null);

        let secondsRead = 0;
        const intervalId = setInterval(() => {
            if (document.hidden) {
                return;
            }

            secondsRead += 5;
            trackWithBootstrap(props.analyticsBootstrapUrl, 'trackReadProgress', {
                faqSectionId: props.sectionId,
                articleId: props.article.id,
                secondsRead,
            }).catch(() => null);
        }, 5000);

        return () => clearInterval(intervalId);
    }, [collapsed, props.analyticsBootstrapUrl, props.article.id, props.sectionId]);

    const handleToggle = () => {
        if (isAnimating) return; // Prevent multiple clicks during animation
        
        setIsAnimating(true);
        setCollapsed(!collapsed);
        
        // Reset animation flag after animation completes
        setTimeout(() => {
            setIsAnimating(false);
        }, 300); // Match the CSS transition duration
    };

    // A real button, not a clickable <div>: the row was reachable by mouse
    // only, and gave a screen reader neither the question as a control nor
    // whether its answer was open.
    return (
        <div className="hc-faqs-article-item hc-expandable-article">
            <button type="button"
                    className={cx("hc-faqs-article-item-title d-flex justify-content-between align-items-center", {
                        "animating": isAnimating
                    })}
                    aria-expanded={!collapsed}
                    aria-controls={answerId}
                    onClick={handleToggle}>
                <strong>{props.article.title}</strong>
                <i className={cx("fas", collapsed ? 'fa-plus' : 'fa-minus', {
                       "rotated": !collapsed
                   })} aria-hidden="true" />
            </button>
            {/* A collapsed answer stays mounted so its height can animate, so
                `inert` is what keeps its links out of the tab order and its
                text out of the accessibility tree. */}
            <div
                ref={contentRef}
                id={answerId}
                inert={collapsed ? '' : undefined}
                className={cx("hc-expandable-content-wrapper", {
                    "collapsed": collapsed,
                    "expanded": !collapsed
                })}
                style={{height: height + 'px'}}>
                <div className={cx('hc-faqs-article-item-content')} 
                     dangerouslySetInnerHTML={{__html: props.article.content}} />
            </div>
        </div>
    );
};

export default class FullFAQSection extends Component {
    constructor(props) {
        super(props);

        const options = window.hcOptions || {};
        this.defaultLanguage = null;

        this.state = {
            section: [],
            data: [],
            activeCategory: {},
            loading: true,
            unavailable: null,
            lang: options.lang || 'default'
        };

        this.trackedVisibleArticles = new Set();
    }

    componentDidMount() {
        loadFAQSectionData(this.props.id, data => {
            this.defaultLanguage = data.defaultLanguage;
            this.setState({
                section: data.section,
                data: data.categories,
                loading: false,
                activeCategory: data.categories && data.categories.length ? data.categories[0] : {},
                lang: this.state.lang === data.defaultLanguage ? 'default' : this.state.lang,
            });

            trackWithBootstrap(getFAQAnalyticsBootstrapUrl(this.props.id), 'trackPageView', {
                faqSectionId: this.props.id,
            }).catch(() => null);
        }, this.props.isPreview || false, this.state.lang, (reason) => {
            // Render nothing (no section title) instead of a preloader that
            // never resolves — see loadFAQSectionData() — unless the IP
            // allow-list refused the visitor, who is told why.
            this.setState({loading: false, unavailable: faqRefusalNotice(reason)});
        });
    }

    onSelectCategory(category) {
        return (e) => {
            e.preventDefault();

            this.setState({
                activeCategory: category
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const layout = this.state.section.options && this.state.section.options.layout ?
            this.state.section.options.layout : 'default';

        if (layout === 'sequential' || !this.state.activeCategory || prevState.activeCategory === this.state.activeCategory) {
            return;
        }

        map(this.state.activeCategory.articles || [], (article) => {
            const trackingKey = `${this.props.id}:${article.id}`;
            if (this.trackedVisibleArticles.has(trackingKey)) {
                return;
            }

            this.trackedVisibleArticles.add(trackingKey);
            trackWithBootstrap(getFAQAnalyticsBootstrapUrl(this.props.id), 'trackArticleView', {
                faqSectionId: this.props.id,
                articleId: article.id,
                articleViewType: 'faq_expand',
            }).catch(() => null);
        });
    }

    renderDefaultLayout() {
        return (
            <div className={cx('hc-faqs-'+this.props.id, 'hc-faqs-default')}>
                <div className={cx({'hc-faqs-categories':true})}>
                    <h3 className={cx('hc-faqs-title')}>{sectionText(this.state.section.title, this.state.lang)}</h3>
                    {map(this.state.data, (category, indx) => {
                        const isActive = !!this.state.activeCategory && this.state.activeCategory.id === category.id;

                        return <a className={cx({'hc-faqs-category': true, active: isActive})}
                                aria-current={isActive ? 'true' : undefined}
                                href="#" key={'hc-faq-category-' + category.id} onClick={this.onSelectCategory(category)}>{category.name}</a>;
                    })}
                </div>
                <div className="hc-faqs-articles">
                    {this.state.activeCategory ? map(this.state.activeCategory.articles, (article, indx) => {
                        return <div className="hc-faqs-article-item" key={'article-' + article.id}>
                            <div className="hc-faqs-article-item-title"><strong>{article.title}</strong></div>
                            <div className={cx('hc-faqs-article-item-content')} dangerouslySetInnerHTML={{__html: article.content}} />
                        </div>;
                    }): <p>No FAQs found.</p>}
                </div>
            </div>
        );
    }

    renderSequentialLayout() {
        return (
            <div className={cx('hc-faqs-'+this.props.id, 'hc-faqs-sequential')}>
                <h3 className={cx('hc-faqs-title')}>{sectionText(this.state.section.title, this.state.lang)}</h3>
                {map(this.state.data, (category, categoryIndex) => {
                    return (
                        <div key={'category-' + category.id} className={cx('hc-faqs-category-section')}>
                            <h4 className={cx('hc-faqs-category-title')}>
                                {category.name}
                            </h4>
                            <div className="hc-faqs-category-articles">
                                {map(category.articles, (article, articleIndex) => {
                                    return <ExpandableArticleItem
                                        key={'expandable-article-' + article.id}
                                        article={article}
                                        analyticsBootstrapUrl={getFAQAnalyticsBootstrapUrl(this.props.id)}
                                        sectionId={this.props.id}
                                    />;
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const preloader = <div className="text-center"><i className="fas fa-spinner fa-spin"></i> FAQ section is loading...</div>;
        
        if (this.state.loading) {
            return preloader;
        }

        if (this.state.unavailable) {
            return <p className="hc-faqs-unavailable" role="status">{this.state.unavailable}</p>;
        }
        
        if (!this.state.section.title) {
            return null;
        }
        
        if (this.state.data.length === 0) {
            return <p>There aren't any FAQs here, yet.</p>;
        }
        
        // Determine layout type from section options; the design studio
        // passes the one being edited before it is saved.
        const layout = this.props.layout || (this.state.section.options && this.state.section.options.layout ?
            this.state.section.options.layout : 'default');
        
        if (layout === 'sequential') {
            return this.renderSequentialLayout();
        } else {
            return this.renderDefaultLayout();
        }
    }
}
